import React, { useMemo, useState } from 'react';
import { Autocomplete, InputBase, Popper } from '@mui/material';
import Icon from '../../muiComponents/Icon';
import '../../stylesheets/stylesComponents/searchbarTypeahead.scss';

// export interface ISearchBarTypeaheadOption {
//     id: string | number;
//     label: string;
//     value: string | number;
//     group?: string;
// }

export interface ISearchBarTypeaheadOption<T> {
    id: string | number;
    label: string;
    value: string | number;
    group?: string;
    data?: T;
}

interface SearchBarTypeaheadProps<T> {
    placeholder?: string;
    options: ISearchBarTypeaheadOption<T>[];
    value?: ISearchBarTypeaheadOption<T> | null;
    idValue?: string;
    disabled?: boolean;
    noOptionsText?: string;
    groupBy?: (option: ISearchBarTypeaheadOption<T>) => string;
    onSelect?: (option: ISearchBarTypeaheadOption<T> | null) => void;
    onInputChange?: (value: string) => void;
    resetInputValueOnSelect?: boolean;
}

const SearchBarTypeahead = <T,>({
    placeholder,
    options,
    value = null,
    idValue,
    disabled = false,
    noOptionsText = 'No options',
    groupBy,
    onSelect,
    onInputChange,
    resetInputValueOnSelect = false,
}: SearchBarTypeaheadProps<T>) => {
    const [inputValue, setInputValue] = useState('');

    const hasGroups = useMemo(
        () => Boolean(groupBy) || options.some(option => Boolean(option.group)),
        [groupBy, options]
    );

    const selectedValueOption = useMemo(() => {
        if (!value && !idValue) return null;
        return options.find(option => {
            if (value) return option.value === value.value;
            if (idValue) return option.id === parseInt(idValue);
            return false;
        });
    }, [value, idValue, options]);

    return (
        <Autocomplete
            disablePortal
            disabled={disabled}
            options={options}
            value={selectedValueOption}
            inputValue={inputValue}
            noOptionsText={noOptionsText}
            openOnFocus
            autoHighlight
            getOptionLabel={option => option.label}
            isOptionEqualToValue={(option, selected) =>
                option.value === selected.value
            }
            filterOptions={(allOptions, state) => {
                const term = state.inputValue.trim().toLowerCase();
                if (!term) return allOptions;

                return allOptions.filter(option => {
                    const text = option.label.toLowerCase();
                    const group = (
                        groupBy ? groupBy(option) : option.group || ''
                    ).toLowerCase();

                    return text.includes(term) || group.includes(term);
                });
            }}
            onInputChange={(_event, newInputValue) => {
                setInputValue(newInputValue);
                onInputChange?.(newInputValue);
            }}
            onChange={(_event, selectedOption) => {
                onSelect?.(selectedOption);
                if (resetInputValueOnSelect) {
                    setInputValue('');
                    onInputChange?.('');
                }
            }}
            {...(hasGroups
                ? {
                      groupBy: (option: ISearchBarTypeaheadOption<T>) =>
                          groupBy ? groupBy(option) : option.group || 'Other',
                  }
                : {})}
            PopperComponent={props => (
                <Popper
                    {...props}
                    className='searchbar-typeahead__popper'
                    placement='bottom-start'
                />
            )}
            renderInput={params => (
                <div
                    ref={params.InputProps.ref}
                    className='searchbar__container'
                >
                    <InputBase
                        placeholder={placeholder}
                        aria-label={placeholder || 'Search'}
                        className='searchbar__input searchbar-typeahead__input'
                        inputProps={{
                            ...params.inputProps,
                        }}
                        disabled={disabled}
                    />
                    <div className='flex flex-gap-10'>
                        {inputValue ? (
                            <Icon
                                iconName='Close'
                                onClick={() => {
                                    setInputValue('');
                                    onInputChange?.('');
                                }}
                                style={{ color: '#727272', cursor: 'pointer' }}
                            />
                        ) : null}
                        <Icon iconName='Search' style={{ color: '#727272' }} />
                    </div>
                </div>
            )}
            renderOption={(props, option) => (
                <li {...props}>
                    <span className='searchbar-typeahead__option-label'>
                        {option.label}
                    </span>
                </li>
            )}
            renderGroup={params => (
                <li key={params.key} className='searchbar-typeahead__group'>
                    <div className='searchbar-typeahead__group-title'>
                        {params.group}
                    </div>
                    <ul>{params.children}</ul>
                </li>
            )}
        />
    );
};

export default React.memo(SearchBarTypeahead);
